import React, { useContext, useMemo } from 'react';
import { Box, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Events, Properties } from '../../../../enums/Analytics';
import {
  markWelcomeMessageAsViewed,
  shouldDisplayWelcomeMessageOverlay,
} from './helper';
import { RoomViewContext } from '../../View/RoomViewContext';
import {
  UserSchema,
  WelcomeMessageSchema,
} from '../../../../definitions/schemas';
import Analytics from '../../../../tools/Analytics';
import ContentType from '../../../../enums/ContentType';
import WelcomeMessageDialog from './WelcomeMessageDialog';
import WelcomeMessagePlayerFacade from './WelcomeMessagePlayer';
import UserStore from '../../../../stores/observables/UserStore';

const useStyles = makeStyles((theme: Theme) => ({
  welcomeMessageContainer: {
    borderRadius: theme.spacing(1),
  },
  welcomeMessageVideo: {
    borderRadius: theme.spacing(1),
  },
}));

export interface WelcomeMessageProps {
  disableWelcomeMessageOverlay?: boolean;
  owner: UserSchema | undefined;
  referrer: string;
  roomId: string;
  track: boolean;
  userContactId?: string;
  welcomeMessage: WelcomeMessageSchema;
}

const WelcomeMessage: React.FC<WelcomeMessageProps> = (
  props: WelcomeMessageProps
): JSX.Element => {
  const { isGuestVisitor } = useContext(RoomViewContext);

  const classes = useStyles();

  const {
    disableWelcomeMessageOverlay = false,
    owner,
    referrer,
    roomId,
    track,
    userContactId,
    welcomeMessage,
  } = props;

  const currentUser = UserStore.get();
  const visitorId = userContactId || currentUser?.id;

  const showWelcomeMessageOverlay = useMemo(
    () =>
      isGuestVisitor &&
      !disableWelcomeMessageOverlay &&
      !!visitorId &&
      shouldDisplayWelcomeMessageOverlay(visitorId, welcomeMessage),
    [disableWelcomeMessageOverlay, isGuestVisitor, visitorId, welcomeMessage]
  );

  const onOverlayWelcomeMessageViewed = () => {
    if (!visitorId) {
      return;
    }

    markWelcomeMessageAsViewed(visitorId, welcomeMessage);

    Analytics.track(Events.WELCOME_MESSAGE_OVERLAY_VIEWED, {
      [Properties.CONTENT_TYPE]: welcomeMessage?.type,
      [Properties.ENTERED_FROM]: referrer,
    });
  };

  return (
    <Box borderRadius="undefinedpx" mb={2} overflow="hidden" width="100%">
      <WelcomeMessageDialog
        disableOwner={!owner}
        displayWelcomeMessageOverlay={showWelcomeMessageOverlay}
        onWelcomeMessageViewed={onOverlayWelcomeMessageViewed}
        widthAuto={welcomeMessage.type !== ContentType.VIDEO}
      >
        <WelcomeMessagePlayerFacade
          contactId={userContactId}
          content={welcomeMessage}
          contentId={welcomeMessage.id!}
          fixedVideoHeight
          owner={owner}
          referrer={referrer}
          roomId={roomId}
          track={track}
          version={welcomeMessage.version}
          videoClasses={{
            container: classes.welcomeMessageContainer,
            video: classes.welcomeMessageVideo,
          }}
          withVideoCover
        />
      </WelcomeMessageDialog>
    </Box>
  );
};

export default WelcomeMessage;
